// Component: TemplateRow
// Purpose: Single row renderer for a template with preview, metadata, and
// actions (edit/delete). Emits delete and preview events.
import {
  ArrowUpRight,
  Calendar,
  Check,
  Edit,
  Globe,
  LayoutGrid,
  LayoutPanelTop,
  Tag,
  Trash2,
  Video,
  X,
  ZoomIn,
} from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ButtonSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';

export default function TemplateRow({ template, onDelete, onPreviewImage, isDeletingId }) {
  const navigate = useNavigate();
  const [isDeleteConfirming, setIsDeleteConfirming] = useState(false);
  
  // Check if this template is currently being deleted
  const isDeleting = isDeletingId === template.id;
  
  // Convert Firestore timestamp to Date properly
  let formattedDate = 'Invalid date';
  try {
    if (template.datePublished?._seconds) {
      // Handle Firestore timestamp object
      const publishDate = new Date(template.datePublished._seconds * 1000);
      formattedDate = publishDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } else if (template.datePublished?.toDate) {
      // Handle Firestore timestamp
      const publishDate = template.datePublished.toDate();
      formattedDate = publishDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } else if (template.datePublished) {
      // Handle regular date
      const publishDate = new Date(template.datePublished);
      formattedDate = publishDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  } catch (error) {
    console.error('Error formatting date:', error);
  }

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (isDeleting) {
      return; // Do nothing if already deleting
    }
    
    if (isDeleteConfirming) {
      onDelete(template.id);
      setIsDeleteConfirming(false);
    } else {
      setIsDeleteConfirming(true);
    }
  };

  const cancelDelete = (e) => {
    e.stopPropagation();
    setIsDeleteConfirming(false);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    navigate(`/app/private/editTemplate/${template.id}`);
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
    if (onPreviewImage) {
      onPreviewImage(template);
    }
  };

  return (
    <div className="group flex flex-col border-b border-gray-200 p-4 dark:border-gray-700 sm:flex-row sm:items-center hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all duration-200">
      {/* Template Preview Image - now clickable */}
      <div 
        className="relative mr-4 h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 dark:border-gray-700 cursor-pointer group transition-transform duration-200 hover:scale-105"
        onClick={handleImageClick}
      >
        {template.previewImageURL ? (
          <img
            src={template.previewImageURL}
            alt={`${template.company} template preview`}
            className="h-full w-full object-cover"
            onError={(e) => {
              console.error(`Failed to load image for template: ${template.id}`);
              e.target.src = 'https://via.placeholder.com/64x64?text=No+Image';
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gray-100 dark:bg-gray-700">
            <span className="text-xs text-gray-500 dark:text-gray-400">No image</span>
          </div>
        )}
        {/* Overlay with zoom icon on hover */}
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 transition-all duration-200 group-hover:bg-opacity-40">
          <ZoomIn className="h-5 w-5 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
        </div>
      </div>

      {/* Template Info */}
      <div className="flex-1 sm:flex sm:items-center sm:justify-between">
        <div className="mt-4 sm:mt-0">
          <h3 className="text-base font-medium text-gray-900 dark:text-white">
            {template.company}
          </h3>
          
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
            {/* Template Type - updated from Size */}
            <div className="flex items-center gap-1.5">
              {template.type === 'graphic' || template.type === 'Ad' ? (
                <LayoutGrid className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              ) : template.type === 'website' || template.type === 'Email' ? (
                <Globe className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              ) : template.type === 'video' || template.type === 'Video' ? (
                <Video className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              ) : template.type === 'print' || template.type === 'Landing' ? (
                <LayoutPanelTop className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              ) : (
                <LayoutGrid className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              )}
              <span className="capitalize">{template.type || template.size}</span>
            </div>
            
            {/* Publish Date */}
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <span>{formattedDate}</span>
            </div>
            
            {/* Tags */}
            <div className="flex items-center gap-1.5">
              <Tag className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <span>
                {template.tags.slice(0, 2).join(', ')}
                {template.tags.length > 2 ? ` +${template.tags.length - 2} more` : ''}
              </span>
            </div>
            
            {/* Total Clicks */}
            <div className="flex items-center gap-1.5">
              <ArrowUpRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <span>{template.totalClicks || 0} clicks</span>
            </div>
          </div>
        </div>

        {/* Action Buttons - Always show Edit/Delete */}
        <div className="mt-4 flex items-center gap-2 sm:mt-0 sm:ml-4">
          {isDeleteConfirming && !isDeleting ? (
            <>
              <span className="hidden text-sm text-red-600 dark:text-red-400 sm:block">
                Are you sure?
              </span>
              <button
                onClick={handleDeleteClick}
                className="inline-flex items-center rounded-md border border-transparent bg-red-100 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
                disabled={isDeleting}
              >
                <Check className="mr-1.5 h-3.5 w-3.5" />
                Yes
              </button>
              <button
                onClick={cancelDelete}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                disabled={isDeleting}
              >
                <X className="mr-1.5 h-3.5 w-3.5" />
                No
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleEditClick}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                disabled={isDeleting}
              >
                <Edit className="mr-1.5 h-3.5 w-3.5" />
                Edit
              </button>
              <button
                onClick={handleDeleteClick}
                className="inline-flex items-center rounded-md border border-transparent bg-red-100 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <ButtonSpinner color="current" className="mr-1.5 h-3.5 w-3.5" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                    Delete
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
} 